import { useCustomerSearch } from '@/features/customers/hooks/useCustomers';
import { primaryPhone } from '@/features/reservations/model/reservation-form';
import { useT } from '@/i18n';
import { formatPhone } from '@/utils/phone';
import type { Customer } from '@restaurant/api-client';
import { BlockTitle, List, ListItem } from 'framework7-react';
import { useEffect, useState } from 'react';

interface Props {
  name: string;
  phone: string;
  picked: boolean;
  onPick: (customer: Customer) => void;
}

export default function GuestSuggestions({ name, phone, picked, onPick }: Props) {
  const tr = useT();
  const [searchTerm, setSearchTerm] = useState('');

  // Search by whichever identifier the operator has typed - name first, else
  // phone (a caller often gives their number before their name). The backend
  // matches name/email/phone from a single string, so either works.
  useEffect(() => {
    if (picked) {
      setSearchTerm('');
      return;
    }
    const key = name.trim().length >= 2 ? name.trim() : phone.trim();
    const t = setTimeout(() => setSearchTerm(key), 250);
    return () => clearTimeout(t);
  }, [name, phone, picked]);

  const { data: searchData } = useCustomerSearch(searchTerm);
  // Surface returning guests first - the whole point of the picker is to reuse
  // an existing record, and a regular is the most likely match for a typed name.
  const suggestions = [...(searchData?.items ?? [])].sort((a, b) => b.totalVisits - a.totalVisits);
  if (picked || searchTerm.length < 2 || suggestions.length === 0) return null;

  return (
    <>
      <BlockTitle>{tr.createReservation.existingGuests}</BlockTitle>
      <List strongIos insetIos dividersIos>
        {suggestions.map((c) => (
          <ListItem
            key={c.id}
            link="#"
            title={`${c.firstName} ${c.lastName}`.trim()}
            after={formatPhone(primaryPhone(c)) || undefined}
            footer={
              [
                c.email,
                c.totalVisits > 0 ? tr.createReservation.returningGuest(c.totalVisits) : null,
              ]
                .filter(Boolean)
                .join(' · ') || undefined
            }
            onClick={() => onPick(c)}
          />
        ))}
      </List>
    </>
  );
}
